import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Accordion, Icon, Header } from 'semantic-ui-react';
import { format } from 'date-fns';
import { ProjectBidSummary } from '~/types/project';
import { Unit, UnitDecimals } from '~/constants';
import { formatNumber, isPerYearOfftake } from '~/utils';
import { getProjectPvSizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import { normalizeToOverall, normalizeToPerWdc, normalizeToOverallStorage, normalizeToPerKwhStorage } from '~/utils/calculators/costsFns';
import Actions from './Actions';
import BidHistory from '~/features/projectBidding/shared/BidHistory';
import BudgetTable from '~/features/projectReview/components/BudgetTable';
import { ProjectBid } from '~/types/project/ProjectBid';
import RfiResponseList from '~/features/requestForInformation/developer/RfiResponseList';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { InvestorActivityChecklistSingle } from '~/features/portfolioBidding/developer/InvestorActivityChecklist';
import { InvestorActivityChecklist as InvestorActivityChecklistType } from '~/types/portfolio/PortfolioDealSummary';

import '../styles.scss';

interface ProjectBidRowProps {
    projectBidSummary: ProjectBidSummary;
    index: number;
    project: any;
    projectDisplayValues: any;
    activity?: InvestorActivityChecklistType | null;
    hasSummaryDoc?: boolean;
}

const ProjectBidRow: React.FC<ProjectBidRowProps> = ({ projectBidSummary, index, project, projectDisplayValues, activity, hasSummaryDoc }) => {
    const { permissions } = usePermissions();
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const handleAccordionClick = (e: any, titleProps: any) => {
        const { index } = titleProps;
        const newIndex = activeIndex === index ? -1 : index;
        setActiveIndex(newIndex);
    };
    const totalPvSizeKwdc = getProjectPvSizeKwdc(project);
    const totalStorageSizeKwh = getProjectStorageSizeKwh(project);
    const isStorageOnly = totalPvSizeKwdc === 0 && totalStorageSizeKwh > 0;

    // Helper function to convert bid amounts between units
    const convertBidAmount = (bid: ProjectBid, targetUnit: 'TOTAL' | 'WDC' | 'KWH' | 'BESS_KWH' | 'YEAR') => {

        if (!bid?.amount) return 0;

        if (isStorageOnly) {
            // For storage-only projects, assume bids are per KWH or total
            if (targetUnit === 'TOTAL') {
                return bid.unit === Unit.KWH
                    ? normalizeToOverallStorage(totalStorageSizeKwh, bid)
                    : bid.amount;
            } else {
                return bid.unit === Unit.KWH
                    ? bid.amount
                    : normalizeToPerKwhStorage(totalStorageSizeKwh, bid);
            }
        } else {
            if (!totalPvSizeKwdc) return 0;
            if (project.pricingType === 'OFFTAKE') {
                return bid.amount;
            }
            if (targetUnit === 'TOTAL') {
                return bid.unit === Unit.WDC
                    ? normalizeToOverall(totalPvSizeKwdc, bid)
                    : bid.amount;
            } else {
                return bid.unit === Unit.WDC
                    ? bid.amount
                    : normalizeToPerWdc(totalPvSizeKwdc, bid);
            }
        }
    };

    const isDeclined = projectBidSummary?.projectStatus === 'DECLINED_TO_BID' || projectBidSummary?.projectStatus === 'BID_REJECTED';

    // Helper function to format bid amounts for display
    const formatBidAmount = (bid: ProjectBid | null, displayUnit: 'TOTAL' | 'WDC' | 'KWH' | 'BESS_KWH' | 'YEAR') => {
        if (!bid?.amount) {
            return <i style={{ color: '#5a5a5a' }}>{isDeclined ? 'Declined' : 'Awaiting bid...'}</i>;
        }

        if (!totalPvSizeKwdc && !isStorageOnly) {
            return <i style={{ color: '#5a5a5a' }}>-</i>;
        }

        const convertedAmount = convertBidAmount(bid, displayUnit);

        if (displayUnit === 'BESS_KWH') {
            return `$${formatNumber(convertedAmount, 2)}`;
        }
        if (displayUnit === 'KWH') {
            return `$${formatNumber(convertedAmount, 4)}`;
        }
        if (displayUnit === 'YEAR') {
            return `$${formatNumber(convertedAmount, UnitDecimals.YEAR)}`;
        }
        if (displayUnit === 'WDC') {
            return `$${formatNumber(convertedAmount, 3)}`;
        }
        // TOTAL
        return `$${formatNumber(convertedAmount, 0)}`;

    };

    const formatLastUpdated = (data: ProjectBidSummary) => {
        const declined = data.projectStatus === 'DECLINED_TO_BID' || data.projectStatus === 'BID_REJECTED';
        const date = data.bid?.createdAt ?? (declined ? data.projectUpdatedAt : null);
        if (!date) {
            return <i style={{ color: '#5a5a5a' }}>—</i>;
        }
        return format(new Date(date), 'MMM d, yyyy');
    };

    const getInvestorName = (data: ProjectBidSummary) => {
        return data.investor?.company?.name || 'Unknown Investor';
    };

    const bidData = projectBidSummary.bid as ProjectBid;
    const awaitingBid = !bidData?.amount;
    const isAccordionOpen = activeIndex === index;

    // console.log('Rendering bid row for investor:', getInvestorName(data), 'with bidData:', bidData);

    return (
        <React.Fragment>
            <Table.Row className="bid-row">
                <Table.Cell>
                    <Accordion>
                        <Accordion.Title
                            active={isAccordionOpen}
                            index={index}
                            onClick={handleAccordionClick}
                            className="accordion-investor-row"
                        >
                            <Icon name='dropdown' style={{ marginRight: '8px' }} />
                            <div className="investor-info">
                                <span style={{ fontWeight: 'bold' }}>
                                    {getInvestorName(projectBidSummary)}
                                </span>
                                {permissions?.isAdmin && projectBidSummary.investor?.company?.id ? (
                                    <Link
                                        to={`/company-profile/${projectBidSummary.investor.company.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        title="Open company profile in new tab"
                                        style={{ marginLeft: 8, fontSize: 12, textDecoration: 'none' }}
                                    >
                                        <Icon name="external" />
                                    </Link>
                                ) : null}
                            </div>
                        </Accordion.Title>
                    </Accordion>
                </Table.Cell>
                {project?.pricingType === 'OFFTAKE' ? (
                    <Table.Cell textAlign="right">
                        {formatBidAmount(bidData, isPerYearOfftake(project?.contractType) ? 'YEAR' : 'KWH')}
                    </Table.Cell>
                ) : null}
                {project?.pricingType === 'ACQUISITION' || project?.pricingType === 'DEV_FEE' ? (
                    <React.Fragment>
                        {isStorageOnly ? (
                            <Table.Cell textAlign="right">
                                {formatBidAmount(bidData, 'BESS_KWH')}
                            </Table.Cell>
                        ) : (
                            <Table.Cell textAlign="right">
                                {formatBidAmount(bidData, 'WDC')}
                            </Table.Cell>
                        )}
                        <Table.Cell textAlign="right">
                            {formatBidAmount(bidData, 'TOTAL')}
                        </Table.Cell>
                    </React.Fragment>
                ) : null}
                <Table.Cell textAlign="right">
                    {formatLastUpdated(projectBidSummary)}
                </Table.Cell>
                <Table.Cell textAlign="right">
                    <Actions data={projectBidSummary} awaitingBid={awaitingBid} />
                </Table.Cell>
            </Table.Row>
            <Table.Row className="bid-row-accordion" style={{ display: isAccordionOpen ? 'table-row' : 'none', backgroundColor: '#f1f1f1' }}>
                <Table.Cell colSpan={project?.pricingType === 'OFFTAKE' ? 4 : 6}>
                    <div className="accordion-content">

                        {project.pricingType !== 'OFFTAKE' ? (
                            <>
                                <Header as="h5">Budget Table</Header>
                                <BudgetTable project={project} formState={bidData} />
                            </>
                        ) : null}

                        {projectBidSummary.rfiResponses && projectBidSummary.rfiResponses.length > 0 ? (
                            <>
                                <Header as="h5">Supporting Information</Header>
                                <RfiResponseList rfiResponses={projectBidSummary.rfiResponses || []} investorName={getInvestorName(projectBidSummary)} tableView={true} />
                            </>
                        ) : null}



                        {(permissions?.isAdmin || (permissions?.isDeveloper && !project?.portfolioId)) && (
                            <>
                                <Header as="h5">Activity</Header>
                                <InvestorActivityChecklistSingle
                                    activity={activity}
                                    context="project"
                                    showDates={permissions?.isAdmin}
                                    hasSummaryDoc={hasSummaryDoc}
                                />
                            </>
                        )}
                        {permissions?.isAdmin && (
                            <>
                                <Header as="h5">Bid History</Header>
                                <BidHistory project={project} investorId={projectBidSummary.investor.id} />
                            </>
                        )}

                    </div>
                </Table.Cell>
            </Table.Row>
        </React.Fragment>
    );
};

export default ProjectBidRow;
